import { Button } from "@/components/ui/button";
import {
    NavigationMenu,
    NavigationMenuContent,
    NavigationMenuItem,
    NavigationMenuLink,
    NavigationMenuList,
    NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { Menu, MoveRight, X, Ban, CreditCard } from "lucide-react";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { SignInButton, UserButton, SignedIn, SignedOut } from '@clerk/clerk-react';
import TierBadge from "../TierBadge";
import { ContentIcon } from "../icons/ContentIcon";
import { Book, Pencil, FileText } from "lucide-react";
import { useQuery } from 'convex/react';
import { api } from '../../lib/convex';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import SubscriptionManager from "../SubscriptionManager";
import { useToast } from "@/components/ui/use-toast";
import { Input } from "@/components/ui/input";
import { useMutation } from 'convex/react';

function Header1() {
    const location = useLocation();
    const isWriterPage = location.pathname === '/writer';
    const isAdmin = useQuery(api.blog.checkAdminStatus);
    
    const [isOpen, setOpen] = useState(false);
    const [isStrikeListOpen, setIsStrikeListOpen] = useState(false);
    const [isBillingOpen, setIsBillingOpen] = useState(false);
    const preferences = useQuery(api.posts.getUserPreferences);
    const updatePreferences = useMutation(api.posts.updateUserPreferences);
    const { toast } = useToast();
    const [newWords, setNewWords] = useState({
      words: '',
      phrases: '',
      similarities: '',
      literaryDevices: '',
    });
    
    const navigationItems = [
        {
            title: "Home",
            href: "/",
            description: "",
        },
        {
            title: "Blog",
            href: "/blog",
            description: "",
        },
        {
            title: "Why This Tool?",
            href: "/why",
            description: "",
        },
        {
            title: "Resources",
            description: "Guides, tutorials, and helpful resources to get the most out of our platform.",
            items: [
                {
                    title: "Documentation",
                    href: "/docs",
                },
                {
                    title: "Contact Support",
                    href: "/contact",
                },
            ],
        },
    ];

    const handleAddItem = async (category: keyof typeof newWords) => {
      if (!newWords[category].trim()) return;
  
      const categoryMap = {
        words: 'strikeListWords',
        phrases: 'strikeListPhrases',
        similarities: 'strikeListSimilarities',
        literaryDevices: 'strikeListLiteraryDevices'
      };
  
      try {
        const currentList = preferences?.[categoryMap[category]] || [];
        const newItems = newWords[category]
          .split(',')
          .map(item => item.trim())
          .filter(item => item.length > 0);
  
        const uniqueNewItems = [...new Set(newItems)]
          .filter(item => !currentList.includes(item));
  
        if (uniqueNewItems.length === 0) {
          toast({
            title: "No new items",
            description: `All items are already in your ${category} list.`,
            variant: "destructive",
          });
          return;
        }
  
        await updatePreferences({
          [categoryMap[category]]: [...currentList, ...uniqueNewItems],
          serviceTier: preferences?.serviceTier || 'free',
          enableSectionLabeling: preferences?.enableSectionLabeling ?? true,
        });
  
        setNewWords(prev => ({ ...prev, [category]: '' }));
        toast({
          title: "Items added",
          description: `${uniqueNewItems.length} item${uniqueNewItems.length > 1 ? 's' : ''} added to your ${category} list.`,
        });
      } catch (error) {
        toast({
          title: "Error",
          description: `Failed to add items to ${category} list.`,
          variant: "destructive",
        });
      }
    };
  
    const handleRemoveItem = async (category: keyof typeof newWords, itemToRemove: string) => {
      const categoryMap = {
        words: 'strikeListWords',
        phrases: 'strikeListPhrases',
        similarities: 'strikeListSimilarities',
        literaryDevices: 'strikeListLiteraryDevices'
      };
  
      const currentList = preferences?.[categoryMap[category]] || [];
  
      await updatePreferences({
        [categoryMap[category]]: currentList.filter(item => item !== itemToRemove),
        serviceTier: preferences?.serviceTier || 'free',
        enableSectionLabeling: preferences?.enableSectionLabeling ?? true,
      });
    };
    
    return (
        <header className="w-full z-40 fixed top-0 left-0 bg-cream border-b border-black/10">
            <div className="container relative mx-auto min-h-16 flex gap-4 flex-row lg:grid lg:grid-cols-3 items-center">
                <div className="justify-start items-center gap-4 lg:flex hidden flex-row">
                    <NavigationMenu className="flex justify-start items-start">
                        <NavigationMenuList className="flex justify-start gap-4 flex-row">
                            {navigationItems.map((item) => (
                                <NavigationMenuItem key={item.title}>
                                    {item.href ? (
                                        <>
                                            <Link to={item.href} className="no-underline">
                                                <Button variant="ghost">{item.title}</Button>
                                            </Link>
                                        </>
                                    ) : (
                                        <>
                                            <NavigationMenuTrigger className="font-medium text-sm">
                                                {item.title}
                                            </NavigationMenuTrigger>
                                            <NavigationMenuContent className="!w-[450px] p-4">
                                                <div className="flex flex-col lg:grid grid-cols-2 gap-4">
                                                    <div className="flex flex-col h-full justify-between">
                                                        <div className="flex flex-col">
                                                            <p className="text-base">{item.title}</p>
                                                            <p className="text-gray-600 text-sm">
                                                                {item.description}
                                                            </p>
                                                        </div>
                                                        <Button size="sm" asChild className="mt-10 bg-black text-white hover:bg-black/80">
                                                            <Link to="/writer">
                                                                Create Content Now
                                                            </Link>
                                                        </Button>
                                                    </div>
                                                    <div className="flex flex-col text-sm h-full justify-end">
                                                        {item.items?.map((subItem) => (
                                                            <Link
                                                                to={subItem.href}
                                                                key={subItem.title}
                                                                className="flex flex-row justify-between items-center hover:bg-muted py-2 px-4 rounded text-foreground no-underline"
                                                            >
                                                                <span>{subItem.title}</span>
                                                                <MoveRight className="w-4 h-4 text-gray-500" />
                                                            </Link>
                                                        ))}
                                                    </div>
                                                </div>
                                            </NavigationMenuContent>
                                        </>
                                    )}
                                </NavigationMenuItem>
                            ))}
                        </NavigationMenuList>
                    </NavigationMenu>
                </div>
                <div className="flex lg:justify-center">
                    <Link to="/" className="flex items-center gap-2 group">
                        <ContentIcon className="w-10 h-10 text-dogbrown transition-transform duration-300 group-hover:scale-110" />
                        <div className="flex flex-col">
                            <div className="flex flex-col">
                                <span className="text-lg font-bold text-gray-900">AiSEOBlogGenerator.com</span>
                                <span className="text-sm text-gray-600">Not dog sh*t content, finally</span>
                            </div>
                        </div>
                    </Link>
                </div>
                <div className="flex justify-end w-full gap-4 items-center">
                    <SignedIn>
                        <TierBadge />
                        
                        <div className="border-r hidden md:inline h-6"></div>
                        
                        <div className="hidden md:flex">
                            <Button 
                                variant="ghost" 
                                size="icon" 
                                title="Strike List" 
                                onClick={() => setIsStrikeListOpen(true)}
                            >
                                <Ban className="w-4 h-4" />
                            </Button>
                            <Button 
                                variant="ghost" 
                                size="icon" 
                                title="Billing & Subscription"
                                onClick={() => setIsBillingOpen(true)}
                            >
                                <CreditCard className="w-4 h-4" />
                            </Button>
                            <Link to="/writer">
                                <Button variant="ghost" size="icon" title="Content Writer">
                                    <Pencil className="w-4 h-4" />
                                </Button>
                            </Link>
                            <Link to="/posts">
                                <Button variant="ghost" size="icon" title="My Posts">
                                    <FileText className="w-4 h-4" />
                                </Button>
                            </Link>
                            
                            {isAdmin && (
                                <Link to="/admin/blog">
                                    <Button variant="ghost" size="icon" title="Blog Admin">
                                        <Book className="w-4 h-4" />
                                    </Button>
                                </Link>
                            )}
                        </div>
                        
                        <UserButton 
                            afterSignOutUrl="/"
                            appearance={{
                                elements: {
                                    avatarBox: "w-8 h-8 rounded-full ring-2 ring-offset-2 ring-dogblue/20 hover:ring-dogblue/40 transition-all duration-300"
                                }
                            }}
                        />
                    </SignedIn>
                    <SignedOut>
                        <Link to="/blog" className="hidden md:flex items-center gap-1 text-gray-700 hover:text-dogblue transition-colors font-medium">
                            <Book className="w-4 h-4" />
                            <span>Blog</span>
                        </Link>
                        
                        <div className="border-r hidden md:inline h-6"></div>
                        <SignInButton mode="modal">
                            <Button variant="outline">Sign in</Button>
                        </SignInButton>
                        <SignInButton mode="modal">
                            <Button className="bg-black hover:bg-black/80 text-white">Get started</Button>
                        </SignInButton>
                    </SignedOut>
                </div>
                <div className="flex w-12 shrink lg:hidden items-end justify-end">
                    <Button variant="ghost" onClick={() => setOpen(!isOpen)}>
                        {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </Button>
                    {isOpen && (
                        <div className="absolute top-16 border-t flex flex-col w-full right-0 bg-cream shadow-lg py-4 container gap-8 z-50">
                            {navigationItems.map((item) => (
                                <div key={item.title}>
                                    <div className="flex flex-col gap-2">
                                        {item.href ? (
                                            <Link
                                                to={item.href}
                                                className="flex justify-between items-center"
                                                onClick={() => setOpen(false)}
                                            >
                                                <span className="text-lg">{item.title}</span>
                                                <MoveRight className="w-4 h-4 stroke-1 text-gray-500" />
                                            </Link>
                                        ) : (
                                            <p className="text-lg">{item.title}</p>
                                        )}
                                        {item.items &&
                                            item.items.map((subItem) => (
                                                <Link
                                                    key={subItem.title}
                                                    to={subItem.href}
                                                    className="flex justify-between items-center pl-4 border-l-2 border-gray-200 ml-2"
                                                    onClick={() => setOpen(false)}
                                                >
                                                    <span className="text-gray-600">
                                                        {subItem.title}
                                                    </span>
                                                    <MoveRight className="w-4 h-4 stroke-1" />
                                                </Link>
                                            ))}
                                    </div>
                                </div>
                            ))}
                            
                            <SignedIn>
                                <div className="grid grid-cols-2 gap-2 mt-2">
                                    <Button 
                                        variant="outline" 
                                        className="w-full flex items-center gap-2"
                                        onClick={() => {
                                            setOpen(false);
                                            setIsStrikeListOpen(true);
                                        }}
                                    >
                                        <Ban className="w-4 h-4" />
                                        Strike List
                                    </Button>
                                    <Button 
                                        variant="outline" 
                                        className="w-full flex items-center gap-2"
                                        onClick={() => {
                                            setOpen(false);
                                            setIsBillingOpen(true);
                                        }}
                                    >
                                        <CreditCard className="w-4 h-4" />
                                        Billing
                                    </Button>
                                    <Link to="/writer" onClick={() => setOpen(false)}>
                                        <Button variant="outline" className="w-full flex items-center gap-2">
                                            <Pencil className="w-4 h-4" />
                                            Writer
                                        </Button>
                                    </Link>
                                    <Link to="/posts" onClick={() => setOpen(false)}>
                                        <Button variant="outline" className="w-full flex items-center gap-2">
                                            <FileText className="w-4 h-4" />
                                            Posts
                                        </Button>
                                    </Link>
                                </div>
                            </SignedIn>
                            
                            <SignedOut>
                                <div className="mt-4 space-y-2">
                                    <SignInButton mode="modal">
                                        <Button variant="outline" className="w-full">Sign in</Button>
                                    </SignInButton>
                                    <SignInButton mode="modal">
                                        <Button className="w-full bg-black hover:bg-black/80 text-white">Get started</Button>
                                    </SignInButton>
                                </div>
                            </SignedOut>
                        </div>
                    )}
                </div>
            </div>

            {/* Strike List Dialog */}
            <Dialog open={isStrikeListOpen} onOpenChange={setIsStrikeListOpen}>
                <DialogContent className="sm:max-w-[500px] max-h-[80vh] overflow-hidden flex flex-col bg-cream">
                    <DialogHeader>
                        <DialogTitle>Strike List</DialogTitle>
                        <DialogDescription>
                            Add words, phrases, and writing patterns you want to avoid in your generated content.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-6 py-6 overflow-y-auto px-1">
                        {/* Words Section */}
                        <div className="space-y-4">
                            <div>
                                <h3 className="text-sm font-medium mb-2">Words</h3>
                                <p className="text-sm text-gray-500 mb-2">Individual words to avoid</p>
                                <div className="flex gap-2">
                                    <Input
                                        value={newWords.words}
                                        onChange={(e) => setNewWords({ ...newWords, words: e.target.value })}
                                        placeholder="Enter words, separated by commas"
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') handleAddItem('words');
                                        }}
                                        className="bg-white"
                                    />
                                    <Button onClick={() => handleAddItem('words')} className="bg-black text-white hover:bg-black/80">Add</Button>
                                </div>
                            </div>
                            <div className="h-[250px] overflow-y-auto p-3 bg-cream rounded-lg">
                                {preferences?.strikeListWords?.map((word) => (
                                <div key={word} className="group flex items-center justify-between gap-2 px-3 py-2 bg-white rounded-lg mb-2 last:mb-0 border shadow-sm hover:bg-gray-50 transition-colors">
                                    <span className="text-sm">{word}</span>
                                    <button
                                        onClick={() => handleRemoveItem('words', word)}
                                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                        <X className="w-3.5 h-3.5 text-gray-400 hover:text-red-500 transition-colors" />
                                    </button>
                                </div>
                                ))}
                            </div>
                        </div>

                        {/* Phrases Section */}
                        <div className="space-y-4">
                            <div>
                                <h3 className="text-sm font-medium mb-2">Phrases</h3>
                                <p className="text-sm text-gray-500 mb-2">Common expressions or idioms to avoid</p>
                                <div className="flex gap-2">
                                    <Input
                                        value={newWords.phrases}
                                        onChange={(e) => setNewWords({ ...newWords, phrases: e.target.value })}
                                        placeholder="Enter phrases, separated by commas"
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') handleAddItem('phrases');
                                        }}
                                        className="bg-white"
                                    />
                                    <Button onClick={() => handleAddItem('phrases')} className="bg-black text-white hover:bg-black/80">Add</Button>
                                </div>
                            </div>
                            <div className="h-[250px] overflow-y-auto p-3 bg-cream rounded-lg">
                                {preferences?.strikeListPhrases?.map((phrase) => (
                                <div key={phrase} className="group flex items-center justify-between gap-2 px-3 py-2 bg-white rounded-lg mb-2 last:mb-0 border shadow-sm hover:bg-gray-50 transition-colors">
                                    <span className="text-sm">{phrase}</span>
                                    <button
                                        onClick={() => handleRemoveItem('phrases', phrase)}
                                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                        <X className="w-3.5 h-3.5 text-gray-400 hover:text-red-500 transition-colors" />
                                    </button>
                                </div>
                                ))}
                            </div>
                        </div>

                        {/* Similarities Section */}
                        <div className="space-y-4">
                            <div>
                                <h3 className="text-sm font-medium mb-2">Similarities</h3>
                                <p className="text-sm text-gray-500 mb-2">Overused comparisons or metaphors</p>
                                <div className="flex gap-2">
                                    <Input
                                        value={newWords.similarities}
                                        onChange={(e) => setNewWords({ ...newWords, similarities: e.target.value })}
                                        placeholder="Enter similarities to avoid"
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') handleAddItem('similarities');
                                        }}
                                        className="bg-white"
                                    />
                                    <Button onClick={() => handleAddItem('similarities')} className="bg-black text-white hover:bg-black/80">Add</Button>
                                </div>
                            </div>
                            <div className="h-[250px] overflow-y-auto p-3 bg-cream rounded-lg">
                                {preferences?.strikeListSimilarities?.map((similarity) => (
                                <div key={similarity} className="group flex items-center justify-between gap-2 px-3 py-2 bg-white rounded-lg mb-2 last:mb-0 border shadow-sm hover:bg-gray-50 transition-colors">
                                    <span className="text-sm">{similarity}</span>
                                    <button
                                        onClick={() => handleRemoveItem('similarities', similarity)}
                                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                        <X className="w-3.5 h-3.5 text-gray-400 hover:text-red-500 transition-colors" />
                                    </button>
                                </div>
                                ))}
                            </div>
                        </div>

                        {/* Literary Devices Section */}
                        <div className="space-y-4">
                            <div>
                                <h3 className="text-sm font-medium mb-2">Literary Devices</h3>
                                <p className="text-sm text-gray-500 mb-2">Writing techniques or patterns to avoid</p>
                                <div className="flex gap-2">
                                    <Input
                                        value={newWords.literaryDevices}
                                        onChange={(e) => setNewWords({ ...newWords, literaryDevices: e.target.value })}
                                        placeholder="Enter literary devices to avoid"
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') handleAddItem('literaryDevices');
                                        }}
                                        className="bg-white"
                                    />
                                    <Button onClick={() => handleAddItem('literaryDevices')} className="bg-black text-white hover:bg-black/80">Add</Button>
                                </div>
                            </div>
                            <div className="h-[250px] overflow-y-auto p-3 bg-cream rounded-lg">
                                {preferences?.strikeListLiteraryDevices?.map((device) => (
                                <div key={device} className="group flex items-center justify-between gap-2 px-3 py-2 bg-white rounded-lg mb-2 last:mb-0 border shadow-sm hover:bg-gray-50 transition-colors">
                                    <span className="text-sm">{device}</span>
                                    <button
                                        onClick={() => handleRemoveItem('literaryDevices', device)}
                                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                        <X className="w-3.5 h-3.5 text-gray-400 hover:text-red-500 transition-colors" />
                                    </button>
                                </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Billing Dialog */}
            <Dialog open={isBillingOpen} onOpenChange={setIsBillingOpen}>
                <DialogContent className="sm:max-w-[800px] max-h-[80vh] overflow-y-auto bg-cream">
                    <DialogHeader>
                        <DialogTitle>Billing & Subscription</DialogTitle>
                        <DialogDescription>
                            Manage your subscription and billing settings
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-6">
                        <SubscriptionManager />
                    </div>
                </DialogContent>
            </Dialog>
        </header>
    );
}

export { Header1 };